import { Tooltip_Content, Tooltip_Wrapper } from "./Tooltip.styled";
import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const TOOLTIP_OFFSET_X = 16;
const TOOLTIP_OFFSET_Y = 12;

interface TooltipProps {
	content: string;
	children: ReactNode;
	className?: string;
	disabled?: boolean;
}

export const Tooltip = ({ content, children, className, disabled = false }: TooltipProps) => {
	const triggerRef = useRef<HTMLDivElement>(null);

	const [isOverflow, setIsOverflow] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [rect, setRect] = useState<DOMRect | null>(null);

	useEffect(() => {
		const trigger = triggerRef.current;
		if (!trigger || disabled) return;
		const target = (trigger.firstElementChild as HTMLElement) || trigger;
		const checkOverflow = () => {
			setIsOverflow(target.scrollWidth > target.clientWidth || target.scrollHeight > target.clientHeight);
		};
		checkOverflow();
		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(target);
		return () => resizeObserver.disconnect();
	}, [content, disabled]);

	const handleMouseEnter = () => {
		if (!isOverflow || disabled) return;
		const trigger = triggerRef.current;
		if (!trigger) return;
		setRect(trigger.getBoundingClientRect());
		setIsOpen(true);
	};

	const handleMouseLeave = () => {
		setIsOpen(false);
	};

	return (
		<>
			<Tooltip_Wrapper
				ref={triggerRef}
				className={className}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{children}
			</Tooltip_Wrapper>
			{isOpen &&
				rect &&
				createPortal(
					<Tooltip_Content
						style={{
							left: rect.left + TOOLTIP_OFFSET_X,
							top: rect.bottom + TOOLTIP_OFFSET_Y,
						}}
						initial={{ opacity: 0, y: 4 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 4 }}
						transition={{
							duration: 0.16,
							ease: [0.16, 1, 0.3, 1],
						}}
					>
						{content}
					</Tooltip_Content>,
					document.body,
				)}
		</>
	);
};
